import type { Input } from '../core/Input';
import type { MatchState } from '../sim/MatchState';

// Bottom-middle pill that surfaces who the camera is following while the
// local player is dead, plus left/right cycle hints. Shown whenever
// `Input.setSpectateVisible(true)` would also be set; hidden otherwise.
//
// Glyphs swap based on `Input.getModality()` — keyboard arrows on kbm,
// gamepad left-stick on gamepad, no glyphs on touch (the on-screen
// btnSpectateL/R already convey the affordance there).
//
// Press flash: when the user actually triggers a cycle, the matching
// glyph briefly enters a `held` state. Refreshes if a second press
// arrives mid-flash so a quick double-tap still reads as held.
//
// Visual language is borrowed from TutorialHintBanner so the keycap +
// stick glyphs feel consistent with other in-game prompts.

export type SpectateModality = 'kbm' | 'gamepad' | 'touch';

export interface SpectatePanelUpdate {
  visible: boolean;
  name: string;
  color: string;
  modality: SpectateModality;
}

const FLASH_MS = 140;

const GP_BG = '#2c3242';
const GP_BG_DARK = '#1c2030';

function svgStick(dir: 'left' | 'right'): string {
  // Stick tilted 5 px in the direction of cycle. The container holds the
  // SVG; the .held state on the wrapper does the press translate.
  const cx = dir === 'left' ? 13 : 23;
  return `
    <svg viewBox="0 0 36 36" width="28" height="28" aria-hidden="true">
      <circle cx="18" cy="18" r="15" fill="${GP_BG}" stroke="${GP_BG_DARK}" stroke-width="2"/>
      <circle cx="${cx}" cy="18" r="9" fill="#5a6376" stroke="${GP_BG_DARK}" stroke-width="1.5"/>
      <text x="${cx}" y="22" font-family="Chewy, system-ui, sans-serif" font-size="11"
            text-anchor="middle" fill="white">L</text>
    </svg>
  `.trim();
}

const STYLE_BLOCK = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#spectate-panel {
  position: fixed;
  left: 50%;
  /* Lift above the touch action buttons on small screens; the safe-area
   * inset keeps it clear of iOS home indicators. */
  bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 11;
  pointer-events: none;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: rgba(24, 28, 36, 0.55);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  color: #EEF4F6;
  font-size: 16px;
  letter-spacing: 0.02em;
  opacity: 0;
  transition: opacity 120ms ease;
  max-width: calc(100vw - 32px);
  white-space: nowrap;
}

#spectate-panel.sp-visible {
  opacity: 1;
}

.sp-label {
  color: #b6c0cc;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sp-name-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.sp-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #888;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
  flex: 0 0 auto;
}

.sp-name {
  color: #EEF4F6;
  font-size: 18px;
}

/* Keycap base — same depth + press feel as TutorialHintBanner.thb-key,
 * sized down a touch to keep the pill compact. */
.sp-key {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 28px;
  padding: 0 8px;
  border-radius: 6px;
  background: #d8e4ff;
  color: #2752c8;
  border: 2px solid #b9caf2;
  box-shadow: 0 3px 0 rgba(31, 78, 160, 0.18);
  font-size: 16px;
  line-height: 1;
  transition: transform 80ms ease, background 80ms ease,
              border-color 80ms ease, box-shadow 80ms ease, color 80ms ease;
}

.sp-key.held {
  background: #2752c8;
  color: #ffffff;
  border-color: #1b3d9c;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
  transform: translateY(2px);
}

.sp-key.sp-key-gp {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  padding: 0 2px;
  min-width: 32px;
  height: 32px;
}
.sp-key.sp-key-gp svg {
  display: block;
  filter: drop-shadow(0 2px 0 rgba(0, 0, 0, 0.18));
  transition: transform 80ms ease, filter 80ms ease;
}
.sp-key.sp-key-gp.held {
  background: transparent;
  box-shadow: none;
  transform: none;
}
.sp-key.sp-key-gp.held svg {
  transform: translateY(2px);
  filter: drop-shadow(0 0 4px rgba(255, 220, 120, 0.9))
          drop-shadow(0 1px 0 rgba(0, 0, 0, 0.22));
}

.sp-key.sp-hidden {
  display: none;
}

@media (max-width: 540px) {
  #spectate-panel {
    font-size: 14px;
    padding: 6px 12px;
    gap: 10px;
  }
  .sp-name { font-size: 16px; }
  .sp-label { font-size: 11px; }
}
`;

export class SpectatePanel {
  private root: HTMLElement;
  private container: HTMLElement;
  private leftKey: HTMLElement;
  private rightKey: HTMLElement;
  private dotEl: HTMLElement;
  private nameEl: HTMLElement;

  private currentModality: SpectateModality = 'kbm';
  private currentName = '';
  private currentColor = '';
  private currentVisible = false;

  private leftFlashTimer: number | null = null;
  private rightFlashTimer: number | null = null;

  constructor() {
    this.root = document.getElementById('hud')!;

    // Append our own style + element without clobbering anything Hud has
    // already mounted. Both modules share #hud as a container.
    const style = document.createElement('style');
    style.textContent = STYLE_BLOCK;
    this.root.appendChild(style);

    const el = document.createElement('div');
    el.id = 'spectate-panel';
    el.innerHTML = `
      <span class="sp-key" id="sp-key-left">←</span>
      <span class="sp-name-wrap">
        <span class="sp-dot" id="sp-dot"></span>
        <span class="sp-label">Spectating</span>
        <span class="sp-name" id="sp-name"></span>
      </span>
      <span class="sp-key" id="sp-key-right">→</span>
    `;
    this.root.appendChild(el);
    this.container = el;
    this.leftKey = el.querySelector('#sp-key-left')!;
    this.rightKey = el.querySelector('#sp-key-right')!;
    this.dotEl = el.querySelector('#sp-dot')!;
    this.nameEl = el.querySelector('#sp-name')!;

    this.applyModality(this.currentModality);
  }

  update(next: SpectatePanelUpdate): void {
    if (next.visible !== this.currentVisible) {
      this.currentVisible = next.visible;
      this.container.classList.toggle('sp-visible', next.visible);
    }
    if (next.name !== this.currentName) {
      this.currentName = next.name;
      this.nameEl.textContent = next.name;
    }
    if (next.color !== this.currentColor) {
      this.currentColor = next.color;
      this.dotEl.style.background = next.color;
    }
    if (next.modality !== this.currentModality) {
      this.currentModality = next.modality;
      this.applyModality(next.modality);
    }
  }

  flash(dir: 'left' | 'right'): void {
    // Don't flash anything when the panel isn't visible (stale edges from
    // before death/after respawn).
    if (!this.currentVisible) return;
    const key = dir === 'left' ? this.leftKey : this.rightKey;
    const timerRef: 'leftFlashTimer' | 'rightFlashTimer' =
      dir === 'left' ? 'leftFlashTimer' : 'rightFlashTimer';
    key.classList.add('held');
    if (this[timerRef] !== null) {
      window.clearTimeout(this[timerRef]!);
    }
    this[timerRef] = window.setTimeout(() => {
      key.classList.remove('held');
      this[timerRef] = null;
    }, FLASH_MS);
  }

  dispose(): void {
    if (this.leftFlashTimer !== null) window.clearTimeout(this.leftFlashTimer);
    if (this.rightFlashTimer !== null) window.clearTimeout(this.rightFlashTimer);
    this.container.remove();
  }

  /** Convenience for the three game-loop wires that all derive the same
   *  update args from the same inputs. Keeps call sites to one line. */
  syncFrom(input: Input, match: MatchState, localDead: boolean): void {
    const focused = match.memberById(match.focusId);
    const localId = match.localPlayer().id;
    const visible =
      localDead && match.phase === 'playing' && !!focused && focused.id !== localId;
    this.update({
      visible,
      name: focused?.name ?? '',
      color: focused?.color ?? '#888',
      modality: input.getModality()
    });
  }

  private applyModality(m: SpectateModality): void {
    if (m === 'touch') {
      // Touch devices already have on-screen L/R buttons; keycaps would be
      // redundant clutter. Just hide them — name + dot still show.
      this.leftKey.classList.add('sp-hidden');
      this.rightKey.classList.add('sp-hidden');
      return;
    }
    this.leftKey.classList.remove('sp-hidden');
    this.rightKey.classList.remove('sp-hidden');
    if (m === 'gamepad') {
      this.leftKey.classList.add('sp-key-gp');
      this.rightKey.classList.add('sp-key-gp');
      this.leftKey.innerHTML = svgStick('left');
      this.rightKey.innerHTML = svgStick('right');
    } else {
      this.leftKey.classList.remove('sp-key-gp');
      this.rightKey.classList.remove('sp-key-gp');
      this.leftKey.textContent = '←';
      this.rightKey.textContent = '→';
    }
  }
}
